// Клавиатура у объектов с подписью и «```» для листинга:
//   Enter в названии рисунка - новый абзац после рисунка; в названии таблицы - в первую ячейку;
//   в названии листинга - в код; Tab в названии таблицы - в первую ячейку; ↓ в конце названия листинга - в код;
//   Backspace в начале названия - выделить объект целиком (второй Backspace удалит);
//   «```» + пробел (или «```python» + Enter) в пустом абзаце - листинг.
import { Extension, InputRule, type Editor } from '@tiptap/core';
import { NodeSelection, Plugin, PluginKey, Selection, TextSelection } from '@tiptap/pm/state';
import { newId } from '../../core/schema/util';
import { langById } from './code/langs';
import { focusCode } from './code/views';

const OBJECTS = new Set(['figure', 'stoTable', 'listing']);

function captionCtx(ed: Editor) {
  const { $from, empty } = ed.state.selection;
  if (!empty || $from.parent.type.name !== 'caption' || $from.depth < 2) return null;
  const obj = $from.node(-1);
  if (!OBJECTS.has(obj.type.name)) return null;
  return { $from, obj, objPos: $from.before(-1), atStart: $from.parentOffset === 0, atEnd: $from.parentOffset === $from.parent.content.size };
}

function toFirstCell(ed: Editor, objPos: number) {
  const obj = ed.state.doc.nodeAt(objPos);
  if (!obj) return false;
  let target = -1;
  obj.descendants((n, p) => { if (target < 0 && (n.type.name === 'tableCell' || n.type.name === 'tableHeader')) target = objPos + 1 + p + 2; return target < 0; });
  if (target < 0) return false;
  ed.view.dispatch(ed.state.tr.setSelection(TextSelection.create(ed.state.doc, target)).scrollIntoView());
  return true;
}

function afterObject(ed: Editor, objPos: number) {
  const obj = ed.state.doc.nodeAt(objPos)!;
  const after = objPos + obj.nodeSize;
  const next = ed.state.doc.nodeAt(after);
  let tr = ed.state.tr;
  if (!next || next.type.name !== 'paragraph' || next.content.size) tr = tr.insert(after, ed.state.schema.nodes.paragraph.create());
  tr.setSelection(TextSelection.create(tr.doc, after + 1)).scrollIntoView();
  ed.view.dispatch(tr);
  return true;
}

/** Заменить абзац (верхнего уровня) листингом и перейти в код. */
export function paragraphToListing(ed: Editor, from: number, to: number, lang: string, code = '') {
  const id = newId();
  const node = ed.schema.nodes.listing.create({ id, lang: langById(lang).id, code }, ed.schema.nodes.caption.create());
  ed.view.dispatch(ed.state.tr.replaceWith(from, to, node).scrollIntoView());
  focusCode(id);
}

function topLevelParagraph(ed: Editor) {
  const { $from } = ed.state.selection;
  if ($from.parent.type.name !== 'paragraph') return null;
  const up = $from.node(-1)?.type.name;
  if (up !== 'doc' && up !== 'appendix') return null;
  return { from: $from.before(), to: $from.after(), text: $from.parent.textContent };
}

/** Выделен объект целиком (рисунок, таблица, листинг, разрыв страницы, заголовок структурного элемента): набор не
 *  заменяет его, а начинает новый абзац после него. */
const TYPE_OVER = new Set(['figure', 'stoTable', 'listing', 'pageBreak', 'structHeading']);
function selectedObject(ed: Editor) {
  const sel = ed.state.selection;
  if (!(sel instanceof NodeSelection) || !TYPE_OVER.has(sel.node.type.name)) return null;
  return { pos: sel.from, node: sel.node };
}

/** Набор при выделенном объекте: текст уходит в абзац после объекта (объект и подпись не меняются).
 *  Раньше браузер правил DOM выделенного рисунка сам: рисунок дублировался, подпись стиралась,
 *  следующий абзац «съедался» в подпись копии. */
function typeAfterObject(ed: Editor, text: string) {
  const o = selectedObject(ed);
  if (!o) return false;
  afterObject(ed, o.pos);
  if (text) ed.view.dispatch(ed.state.tr.insertText(text).scrollIntoView());
  return true;
}

const objectTypingKey = new PluginKey('tfcObjectTyping');

export const CaptionKeys = Extension.create({
  name: 'tfcCaptionKeys',
  priority: 1050,
  addProseMirrorPlugins() {
    const ed = this.editor;
    return [new Plugin({
      key: objectTypingKey,
      props: {
        handleKeyDown: (_view, e) => {
          if (e.ctrlKey || e.metaKey || e.altKey || e.isComposing) return false;
          if (!selectedObject(ed)) return false;
          if (e.key === 'Enter') { e.preventDefault(); return typeAfterObject(ed, ''); }
          if (e.key.length === 1) { e.preventDefault(); return typeAfterObject(ed, e.key); }
          return false;
        },
        handleTextInput: (_view, _from, _to, text) => (selectedObject(ed) ? typeAfterObject(ed, text) : false),
        handleDOMEvents: {
          // щелчок в интервал НАД рисунком/таблицей/листингом (поле 12 пт перед рисунком): браузер ставил курсор в
          // подпись под картинкой, и набор уходил в неё - курсор в конец предыдущего абзаца (как в Word)
          mousedown: (view, e) => {
            const me = e as MouseEvent;
            if (me.button !== 0 || me.shiftKey || me.target !== view.dom) return false;
            // блок сразу под точкой щелчка (щелчок пришёлся в интервал между блоками)
            let below: HTMLElement | null = null;
            for (const ch of Array.from(view.dom.children) as HTMLElement[]) {
              if (ch.classList.contains('pg-sp')) continue;
              const r = ch.getBoundingClientRect();
              if (r.bottom <= me.clientY) continue;
              if (r.top > me.clientY) below = ch;
              break;
            }
            if (!below) return false;
            let at = -1;
            try { at = view.posAtDOM(below, 0) - 1; } catch { return false; }
            const n = at >= 0 ? view.state.doc.nodeAt(at) : null;
            if (!n || !OBJECTS.has(n.type.name)) return false;
            const sel = Selection.findFrom(view.state.doc.resolve(at), -1, true);
            me.preventDefault();
            view.dispatch(view.state.tr.setSelection(sel || NodeSelection.create(view.state.doc, at)));
            view.focus();
            return true;
          },
          beforeinput: (_view, e) => {
            const ie = e as InputEvent;
            if (!selectedObject(ed) || !ie.inputType?.startsWith('insert')) return false;
            if (ie.inputType === 'insertFromPaste' || ie.inputType === 'insertFromDrop') return false;
            ie.preventDefault();
            typeAfterObject(ed, ie.inputType === 'insertText' ? ie.data || '' : '');
            return true;
          },
        },
      },
    })];
  },
  addKeyboardShortcuts() {
    return {
      Enter: () => {
        const ed = this.editor;
        const c = captionCtx(ed);
        if (c) {
          const t = c.obj.type.name;
          if (t === 'stoTable') return toFirstCell(ed, c.objPos);
          if (t === 'listing') { focusCode(c.obj.attrs.id, 'start'); return true; }
          return afterObject(ed, c.objPos);
        }
        const p = topLevelParagraph(ed);
        const m = p && p.text.match(/^```\s*([\w+#.-]*)\s*$/);
        if (p && m) { paragraphToListing(ed, p.from, p.to, m[1]); return true; }
        return false;
      },
      Tab: () => {
        const c = captionCtx(this.editor);
        if (c && c.obj.type.name === 'stoTable') return toFirstCell(this.editor, c.objPos);
        if (c && c.obj.type.name === 'listing') { focusCode(c.obj.attrs.id, 'start'); return true; }
        return false;
      },
      ArrowDown: () => {
        const c = captionCtx(this.editor);
        if (c && c.atEnd && c.obj.type.name === 'listing') { focusCode(c.obj.attrs.id, 'start'); return true; }
        return false;
      },
      Backspace: () => {
        const ed = this.editor;
        const c = captionCtx(ed);
        if (!c || !c.atStart) return false;
        ed.view.dispatch(ed.state.tr.setSelection(NodeSelection.create(ed.state.doc, c.objPos)));
        return true;
      },
    };
  },
  addInputRules() {
    return [new InputRule({
      find: /^```([\w+#.-]*)\s$/,
      handler: ({ state, range, match }) => {
        const $f = state.doc.resolve(range.from);
        const up = $f.node(-1)?.type.name;
        if ($f.parent.type.name !== 'paragraph' || (up !== 'doc' && up !== 'appendix')) return null;
        if ($f.parent.textContent.replace(/\s+$/, '') !== match[0].replace(/\s+$/, '')) return null;
        const id = newId();
        const node = state.schema.nodes.listing.create({ id, lang: langById(match[1] || '').id, code: '' }, state.schema.nodes.caption.create());
        state.tr.replaceWith($f.before(), $f.after(), node);
        focusCode(id);
        return undefined;
      },
    })];
  },
});
